import React, { useEffect } from "react";
import {
    Alert,
    Box,
    Card,
    CardContent,
    Collapse,
    Divider,
    IconButton,
    Typography,
    FormControl,
    Button,
    MenuItem
} from "@mui/material";
import FeatherIcon from "feather-icons-react";

import PageContainer from "../../../components-v2/container/PageContainer";
import Breadcrumb from "../../../layouts-v2/full-layout/breadcrumb/Breadcrumb";

import CustomFormLabel from "../../../components-v2/forms/custom-elements/CustomFormLabel";
import CustomSelect from "../../../components-v2/forms/custom-elements/CustomSelect";
import CustomTextField from "../../../components-v2/forms/custom-elements/CustomTextField";
import OrganizationUserService from "../../../services-v2/eitri-manager-api/OrganizationUserService";
import UserService from "../../../services-v2/blind-guardian-api/UserService";
import { Roles } from "../../../utils-v2/enums/Roles";
import Storage from "../../../services-v2/storage/Storage";
import Validate from "../../../utils-v2/validate/Validate";
import useAlert from "../../../hooks/useAlert";
import useStatus from "../../../hooks/useStatus";

import Loading from "../../../layouts-v2/loading/Loading";

const UserCreate = () => {
    const [orgId, setOrgId] = React.useState("");
    const [email, setEmail] = React.useState("");
    const [role, setRole] = React.useState("");
    const [roles, setRoles] = React.useState([]);
    const [isEmailValid, setIsEmailValid] = React.useState(true);
    const { alertInfo, setAllAlertInfo, setInitialAlertInfo } = useAlert();
    const { status, setAllStatus } = useStatus();

    useEffect(() => {
        setAllStatus({ isFetching: true, success: false, error: false });
        initializeView();
    }, []);

    const initializeView = async () => {
        try {
            findOrgId();
            await findOrgUserRoles();
            setAllStatus({ isFetching: false, success: true, error: false });
        } catch (e) {
            setAllStatus({ isFetching: false, success: false, error: true });
            setAllAlertInfo({
                theme: "error",
                message: e.message,
                visible: true
            });
        }
    };

    const findOrgId = () => {
        const organizationStorage = Storage.get(Storage.KEYS.ORGANIZATION_DATA);
        const organizationId = JSON.parse(organizationStorage)?.id;
        setOrgId(organizationId);
    };

    const findOrgUserRoles = async () => {
        try {
            const roles = await OrganizationUserService.getOrgUserRoles();
            const roleList = roles.map(role => {
                const item = Roles[role];
                return {
                    value: role,
                    label: item
                };
            });
            setRoles(roleList);
        } catch (e) {
            console.error("Ocorreu um erro ao tentar recuperar as roles.", e);
            throw new Error(
                "O sistema não está funcionando conforme esperado. Já estamos trabalhando para ajustar esse desconforto."
            );
        }
    };

    const createUser = async e => {
        e.preventDefault();
        try {
            const isEmailValid = Validate.isEmailValid(email);

            if (!isEmailValid) {
                setIsEmailValid(false);
                return;
            }

            setIsEmailValid(true);
            const id = await geUserId(email);
            if (!id) {
                throw new Error(
                    `O usuário[${email}] não pode ser autenticado.`
                );
            }
            const userToCreate = {
                email: email,
                userId: id,
                organizationId: orgId,
                role: role
            };

            await OrganizationUserService.createOrganizationUser(userToCreate);
            setAllAlertInfo({
                theme: "success",
                message: "Usuário Criado com sucesso.",
                visible: true
            });
        } catch (e) {
            setAllAlertInfo({
                theme: "error",
                message: "Ops! Não foi possível criar o usuário.",
                visible: true
            });
            console.error(
                "Ocorreu um erro ao tentar criar usuário: ",
                e.message
            );
        }
    };

    const geUserId = async email => {
        try {
            const user = await UserService.createUser(email);
            return user.id;
        } catch (e) {
            console.error(
                "Ocorreu um erro ao tentar acessar o id do usuário blind guardian.",
                e
            );
            throw new Error(
                `O usuário[${email}] não tem autenticidade para ser adicinado à organização[${orgId}].`
            );
        }
    };

    const BCrumb = [
        {
            title: "Adicionar Usuário"
        }
    ];

    return (
        <>
            {status.isFetching ? (
                <Loading isVisible={status.isFetching} />
            ) : (
                <PageContainer
                    title="Adicionar Usuário"
                    description="this is Customer Edit page"
                >
                    <Breadcrumb items={BCrumb} />
                    <div>
                        <Card
                            sx={{
                                p: 0
                            }}
                        >
                            <Box
                                sx={{
                                    padding: "15px 30px"
                                }}
                                display="flex"
                                alignItems="center"
                            >
                                <Box flexGrow={1}>
                                    <Typography fontWeight="500" variant="h4">
                                        Adicionar Usuário
                                    </Typography>
                                </Box>
                            </Box>
                            <Divider />
                            <Collapse in={alertInfo.visible}>
                                <Alert
                                    variant="filled"
                                    severity={alertInfo.theme || "info"}
                                    action={
                                        <IconButton
                                            aria-label="close"
                                            color="inherit"
                                            size="small"
                                            onClick={() => {
                                                setInitialAlertInfo();
                                            }}
                                        >
                                            <FeatherIcon icon="x" width="20" />
                                        </IconButton>
                                    }
                                >
                                    {alertInfo.message}
                                </Alert>
                            </Collapse>
                            <CardContent
                                sx={{
                                    padding: "30px"
                                }}
                            >
                                <form onSubmit={createUser}>
                                    <FormControl fullWidth error={true}>
                                        <CustomFormLabel
                                            sx={{
                                                mt: 0
                                            }}
                                            htmlFor="email-address"
                                        >
                                            Email
                                        </CustomFormLabel>
                                        <CustomTextField
                                            required
                                            id="email-address"
                                            variant="outlined"
                                            size="small"
                                            fullWidth
                                            value={email}
                                            helperText={
                                                !isEmailValid &&
                                                "Digite um email válido"
                                            }
                                            error={!isEmailValid}
                                            onChange={e =>
                                                setEmail(e.target.value)
                                            }
                                        />
                                    </FormControl>
                                    <CustomFormLabel htmlFor="role">
                                        Role
                                    </CustomFormLabel>
                                    <CustomSelect
                                        required
                                        fullWidth
                                        id="role"
                                        variant="outlined"
                                        value={role}
                                        onChange={e => setRole(e.target.value)}
                                        size="small"
                                        sx={{
                                            mb: 2
                                        }}
                                    >
                                        {roles.map(option => (
                                            <MenuItem
                                                key={option.value}
                                                value={option.value}
                                            >
                                                {option.label}
                                            </MenuItem>
                                        ))}
                                    </CustomSelect>

                                    <div>
                                        <Button
                                            type="submit"
                                            color="primary"
                                            variant="contained"
                                        >
                                            Salvar
                                        </Button>
                                    </div>
                                </form>
                            </CardContent>
                        </Card>
                    </div>
                </PageContainer>
            )}
        </>
    );
};

export default UserCreate;
